import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../../App';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import UpperCaseInput from '../../../components/UpperCaseInput';
import { Label } from '../../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../components/ui/tooltip';
import { Plus, Pencil, Trash2, Building2, Upload, Users, Search, FileText, Shield, AlertTriangle, AlertCircle, CheckCircle } from 'lucide-react';
import { API_URL, INITIAL_COMPANY_FORM, getExpiryStatus, formatDateDDMMYYYY } from '../utils/constants';

const CompaniesTab = ({ companies, users, admins, onRefresh, onBulkUpload, onAssign, isTransportista, currentUser }) => {
  const { user: authUser } = useAuth();
  const user = currentUser || authUser;
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [formData, setFormData] = useState(INITIAL_COMPANY_FORM);
  const [justification, setJustification] = useState('');
  const [useExternalPermits, setUseExternalPermits] = useState(false);
  
  // Filtros
  const [filterCuit, setFilterCuit] = useState('');
  
  // Filtrar empresas
  const filteredCompanies = useMemo(() => {
    return companies.filter(c => {
      if (filterCuit && !c.cuit_rut?.toLowerCase().includes(filterCuit.toLowerCase())) return false;
      return true;
    });
  }, [companies, filterCuit]);

  // Empresas disponibles para permisos (excluir la empresa actual en edición)
  const availablePermitCompanies = useMemo(() => {
    if (selectedCompany) {
      return companies.filter(c => c.id !== selectedCompany.id);
    }
    return companies;
  }, [companies, selectedCompany]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = { ...formData };
      // Si es TRANSPORTISTA, asignar automáticamente su ID
      if (isTransportista && user?.id) {
        dataToSend.transportista_id = user.id;
      }
      // Si no usa permisos externos, limpiar el campo
      if (!useExternalPermits) {
        dataToSend.permits_company_id = '';
      }
      // Si usa permisos externos, limpiar los campos de permisos propios
      if (useExternalPermits && dataToSend.permits_company_id) {
        dataToSend.permit_holder_name = '';
        dataToSend.international_permit = '';
        dataToSend.permit_expiry = '';
        dataToSend.paut_number = '';
      }
      await axios.post(`${API_URL}/companies`, dataToSend);
      toast.success('Empresa creada exitosamente');
      setIsCreateModalOpen(false);
      resetForm();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear empresa');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!justification.trim()) {
      toast.error('La justificación es obligatoria');
      return;
    }
    try {
      const dataToSend = { ...formData };
      if (!useExternalPermits) {
        dataToSend.permits_company_id = '';
      }
      if (useExternalPermits && dataToSend.permits_company_id) {
        dataToSend.permit_holder_name = '';
        dataToSend.international_permit = '';
        dataToSend.permit_expiry = '';
        dataToSend.paut_number = '';
      }
      await axios.put(`${API_URL}/companies/${selectedCompany.id}`, { justification, data: dataToSend });
      toast.success('Empresa actualizada exitosamente');
      setIsEditModalOpen(false);
      resetForm();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar empresa');
    }
  };

  const handleDelete = async (companyId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta empresa?')) return;
    try {
      await axios.delete(`${API_URL}/companies/${companyId}`);
      toast.success('Empresa eliminada');
      onRefresh();
    } catch (error) {
      toast.error('Error al eliminar empresa');
    }
  };

  const resetForm = () => {
    setFormData(INITIAL_COMPANY_FORM);
    setJustification('');
    setSelectedCompany(null);
    setUseExternalPermits(false);
  };

  const openEditModal = (company) => {
    setSelectedCompany(company);
    const hasExternalPermits = !!company.permits_company_id;
    setUseExternalPermits(hasExternalPermits);
    setFormData({
      name: company.name || '',
      alias: company.alias || '',
      cuit_rut: company.cuit_rut || '',
      address: company.address || '',
      city: company.city || '',
      province: company.province || '',
      transportista_id: company.transportista_id || '',
      permits_company_id: company.permits_company_id || '',
      permit_holder_name: company.permit_holder_name || '',
      international_permit: company.international_permit || '',
      permit_expiry: company.permit_expiry || '',
      paut_number: company.paut_number || ''
    });
    setIsEditModalOpen(true);
  };

  // Obtener nombre de empresa de permisos
  const getPermitsCompanyName = (company) => {
    if (company.permits_company_id) {
      const permitsCompany = companies.find(c => c.id === company.permits_company_id);
      return permitsCompany ? permitsCompany.name : 'Empresa externa';
    }
    return company.permit_holder_name || company.name;
  };

  // Renderizar formulario de empresa (usado en crear y editar)
  const renderCompanyForm = (isEdit = false) => (
    <div className="space-y-6">
      {/* Sección 1: Datos de Facturación */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-slate-700 border-b border-slate-200 pb-2">
          <FileText size={18} className="text-blue-600" />
          <h3 className="font-semibold">Datos de Facturación</h3>
        </div>
        <p className="text-xs text-slate-500">Empresa que emite la factura de transporte</p>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Razón Social *</Label>
            <UpperCaseInput 
              value={formData.name} 
              onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
              required 
              placeholder="Empresa que factura"
            />
          </div>
          <div className="space-y-2">
            <Label>Alias</Label>
            <UpperCaseInput 
              value={formData.alias} 
              onChange={(e) => setFormData({ ...formData, alias: e.target.value })} 
              placeholder="Nombre corto"
            />
          </div>
          <div className="space-y-2">
            <Label>CUIT/RUT *</Label>
            <UpperCaseInput 
              value={formData.cuit_rut} 
              onChange={(e) => setFormData({ ...formData, cuit_rut: e.target.value })} 
              required 
            />
          </div>
          <div className="space-y-2">
            <Label>Dirección *</Label>
            <UpperCaseInput 
              value={formData.address} 
              onChange={(e) => setFormData({ ...formData, address: e.target.value })} 
              required 
            />
          </div>
          <div className="space-y-2">
            <Label>Ciudad *</Label>
            <UpperCaseInput 
              value={formData.city} 
              onChange={(e) => setFormData({ ...formData, city: e.target.value })} 
              required 
            />
          </div>
          <div className="space-y-2">
            <Label>Provincia *</Label>
            <UpperCaseInput 
              value={formData.province} 
              onChange={(e) => setFormData({ ...formData, province: e.target.value })} 
              required 
            />
          </div>
          {/* Campo de selección de Transportista - Solo visible para MASTER/ADMIN */}
          {!isTransportista && (
            <div className="space-y-2">
              <Label>Transportista</Label>
              <Select value={formData.transportista_id || "none"} onValueChange={(value) => setFormData({ ...formData, transportista_id: value === "none" ? "" : value })}>
                <SelectTrigger><SelectValue placeholder="Seleccionar transportista" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin asignar</SelectItem>
                  {users.map(u => (<SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>

      {/* Sección 2: Datos de Permisos */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-slate-700 border-b border-slate-200 pb-2">
          <Shield size={18} className="text-emerald-600" />
          <h3 className="font-semibold">Datos de Permisos Internacionales</h3>
        </div>
        
        {/* Selector: Permisos propios o de otra empresa */}
        <div className="bg-slate-50 p-3 rounded-sm border border-slate-200">
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input 
                type="radio" 
                checked={!useExternalPermits} 
                onChange={() => {
                  setUseExternalPermits(false);
                  setFormData({ ...formData, permits_company_id: '' });
                }}
                className="text-emerald-600"
              />
              <span className="text-sm">Usa permisos propios</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input 
                type="radio" 
                checked={useExternalPermits} 
                onChange={() => setUseExternalPermits(true)}
                className="text-emerald-600"
              />
              <span className="text-sm">Usa permisos de otra empresa</span>
            </label>
          </div>
        </div>

        {useExternalPermits ? (
          /* Seleccionar empresa con permisos */
          <div className="space-y-2">
            <Label>Seleccionar empresa con permisos *</Label>
            <Select 
              value={formData.permits_company_id || "none"} 
              onValueChange={(value) => setFormData({ ...formData, permits_company_id: value === "none" ? "" : value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar empresa dueña de permisos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Seleccionar...</SelectItem>
                {availablePermitCompanies.map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name} ({c.cuit_rut})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">Los datos de permisos se tomarán de la empresa seleccionada</p>
          </div>
        ) : (
          /* Formulario de permisos propios */
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2 col-span-2">
              <Label>Razón Social del Dueño de Permisos</Label>
              <UpperCaseInput 
                value={formData.permit_holder_name} 
                onChange={(e) => setFormData({ ...formData, permit_holder_name: e.target.value })} 
                placeholder="Dejar vacío si es igual al facturador"
              />
              <p className="text-xs text-slate-400">Si es diferente al facturador, indicar aquí</p>
            </div>
            <div className="space-y-2">
              <Label>Permiso Internacional *</Label>
              <UpperCaseInput 
                value={formData.international_permit} 
                onChange={(e) => setFormData({ ...formData, international_permit: e.target.value })} 
                required={!useExternalPermits}
              />
            </div>
            <div className="space-y-2">
              <Label>Vencimiento Permiso *</Label>
              <Input 
                type="date" 
                value={formData.permit_expiry} 
                onChange={(e) => setFormData({ ...formData, permit_expiry: e.target.value })} 
                required={!useExternalPermits}
              />
            </div>
            <div className="space-y-2">
              <Label>N° PAUT *</Label>
              <UpperCaseInput 
                value={formData.paut_number} 
                onChange={(e) => setFormData({ ...formData, paut_number: e.target.value })} 
                required={!useExternalPermits}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="companies-tab">
      <div className="flex items-center justify-between">
        <p className="text-slate-600">
          {isTransportista ? 'Tus empresas de transporte' : 'Gestión de empresas transportistas'}
        </p>
        <div className="flex gap-2">
          {user.role === 'MASTER' && (
            <>
              <Button onClick={() => onBulkUpload('companies')} variant="outline" size="sm" className="text-emerald-600 border-emerald-300 hover:bg-emerald-50">
                <Upload size={16} className="mr-1" /> Carga Masiva
              </Button>
              <Button onClick={() => onAssign('companies')} variant="outline" size="sm" className="text-violet-600 border-violet-300 hover:bg-violet-50">
                <Users size={16} className="mr-1" /> Asignar Admin
              </Button>
            </>
          )}
          <Button onClick={() => setIsCreateModalOpen(true)} data-testid="create-company-button" className="bg-primary hover:bg-primary-hover text-white rounded-sm">
            <Plus className="mr-2" size={18} />Crear Empresa
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Search size={16} className="text-slate-400" />
            <span className="text-sm text-slate-600">Filtrar:</span>
          </div>
          <div className="flex-1 max-w-xs">
            <Input
              placeholder="Buscar por CUIT/RUT..."
              value={filterCuit}
              onChange={(e) => setFilterCuit(e.target.value)}
              className="h-8 text-sm"
            />
          </div>
          {filterCuit && (
            <Button variant="ghost" size="sm" onClick={() => setFilterCuit('')} className="text-slate-500 h-8">
              Limpiar
            </Button>
          )}
          <span className="text-xs text-slate-500">{filteredCompanies.length} de {companies.length}</span>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
        <table className="w-full" data-testid="companies-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Facturador</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Alias</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">CUIT/RUT</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Ciudad</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Dueño Permisos</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Permiso Int.</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Venc. Permiso</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            <TooltipProvider>
              {filteredCompanies.map((company) => {
                const expiryStatus = getExpiryStatus(company.permit_expiry);
                return (
                  <tr key={company.id} className={`hover:bg-slate-50 transition-colors ${expiryStatus.status === 'expired' ? 'bg-red-50' : expiryStatus.status === 'warning' ? 'bg-amber-50' : ''}`} data-testid={`company-row-${company.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Building2 size={16} className="text-slate-400" />
                        <span className="text-sm font-medium text-slate-900">{company.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{company.alias || '-'}</td>
                    <td className="px-4 py-3 text-sm font-mono text-slate-900">{company.cuit_rut}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{company.city}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {company.permits_company_id ? (
                        <span className="text-amber-600 flex items-center gap-1">
                          <Shield size={12} />
                          {getPermitsCompanyName(company)}
                        </span>
                      ) : (
                        <span className="text-emerald-600">{company.permit_holder_name || 'Propios'}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-slate-600">{company.international_permit || '-'}</td>
                    <td className="px-4 py-3">
                      {company.permit_expiry ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className={`flex items-center gap-1.5 text-sm font-mono ${
                              expiryStatus.status === 'expired' ? 'text-red-600 font-semibold' : 
                              expiryStatus.status === 'warning' ? 'text-amber-600 font-semibold' : 'text-slate-600'
                            }`}>
                              {expiryStatus.status === 'expired' ? <AlertCircle size={14} className="text-red-500" /> :
                               expiryStatus.status === 'warning' ? <AlertTriangle size={14} className="text-amber-500" /> :
                               <CheckCircle size={14} className="text-emerald-500" />}
                              {formatDateDDMMYYYY(company.permit_expiry)}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{expiryStatus.label}</p>
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Button onClick={() => openEditModal(company)} variant="ghost" size="sm" className="text-blue-600 hover:bg-blue-50">
                          <Pencil size={16} />
                        </Button>
                        {/* Solo MASTER y ADMIN pueden eliminar, TRANSPORTISTA también puede eliminar sus propias empresas */}
                        {(user.role === 'MASTER' || user.role === 'ADMIN' || (isTransportista && company.transportista_id === user.id)) && (
                          <Button onClick={() => handleDelete(company.id)} variant="ghost" size="sm" className="text-red-600 hover:bg-red-50">
                            <Trash2 size={16} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </TooltipProvider>
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={(open) => { if (!open) resetForm(); setIsCreateModalOpen(open); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Crear Empresa</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            {renderCompanyForm(false)}
            <Button type="submit" className="w-full bg-primary hover:bg-primary-hover text-white">Crear Empresa</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={(open) => { if (!open) resetForm(); setIsEditModalOpen(open); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Editar Empresa</DialogTitle></DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4">
            {renderCompanyForm(true)}
            <div className="space-y-2 pt-4 border-t border-slate-200">
              <Label className="text-red-600">Justificación (obligatoria) *</Label>
              <UpperCaseInput value={justification} onChange={(e) => setJustification(e.target.value)} required placeholder="Motivo de la edición" />
            </div>
            <Button type="submit" className="w-full bg-primary hover:bg-primary-hover text-white">Actualizar Empresa</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompaniesTab;
